import {
  Box,
  Collapse,
  Fade,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography
} from '@mui/material'
import { type MouseEvent, useState } from 'react'
import moment from 'moment'
import { useTranslation } from 'react-i18next'

import Dashboard, { useEmailStats } from './Dashboard'
import HistoryView from './History'
import { createSegmentedControlSx } from './styles'
import {
  type DeliverySourceType,
  type HistorySection,
  type MailerViewMode,
  type StatsPeriodDays
} from './types'


const PERIOD_OPTIONS: StatsPeriodDays[] = [7, 30, 90]

const Mailer = () => {
  const [selectedView, setSelectedView] = useState<MailerViewMode>('graphs')
  const [selectedPeriodDays, setSelectedPeriodDays] = useState<StatsPeriodDays>(30)
  const [initialHistorySection, setInitialHistorySection] = useState<HistorySection>('sends')
  const [initialSendsSourceType, setInitialSendsSourceType] = useState<DeliverySourceType | null>(null)
  const emailStats = useEmailStats({
    periodDays: selectedPeriodDays,
    skip: selectedView !== 'graphs'
  })
  const { t } = useTranslation()
  const handlePeriodChange = (
    _event: MouseEvent<HTMLElement>,
    periodDays: StatsPeriodDays | null
  ) => {
    if (periodDays === null) {
      return
    }

    setSelectedPeriodDays(periodDays)
  }

  const handleViewChange = (
    _event: MouseEvent<HTMLElement>,
    view: MailerViewMode | null
  ) => {
    if (view === null) {
      return
    }

    if (view === 'table') {
      setInitialHistorySection('sends')
      setInitialSendsSourceType(null)
    }

    setSelectedView(view)
  }

  const handleSelectSends = (sourceType?: DeliverySourceType) => {
    setInitialHistorySection('sends')
    setInitialSendsSourceType(sourceType ?? null)
    setSelectedView('table')
  }

  return (
    <Stack
      gap={3}
      sx={{
        p: 3,
        flex: 1,
        minHeight: 0,
        overflow: 'hidden'
      }}
    >
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        justifyContent="space-between"
        gap={2}
        sx={{ flexShrink: 0 }}
      >
        <Typography sx={{ color: '#989898', fontSize: 12, fontWeight: 500 }}>
          {selectedView === 'graphs'
            ? t('mailing.home.title_graph_view', {start_date: moment(emailStats.startDate).format('DD/MM/YYYY'), end_date: moment(emailStats.endDate).format('DD/MM/YYYY')})
            : t('mailing.home.title_graph_default')
          }
        </Typography>

        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          gap={1}
        >
          <ToggleButtonGroup
            value={selectedView}
            exclusive
            onChange={handleViewChange}
            size="small"
            sx={createSegmentedControlSx(88, true)}
          >
            <ToggleButton value="graphs">
              <Box component="i" className="fa-light fa-chart-column" sx={{ mr: 0.75 }} />
              {t('mailing.home.graph')}
            </ToggleButton>
            <ToggleButton value="table">
              <Box component="i" className="fa-light fa-table-list" sx={{ mr: 0.75 }} />
              {t('mailing.home.table')}
            </ToggleButton>
          </ToggleButtonGroup>

          <Collapse
            in={selectedView === 'graphs'}
            orientation="horizontal"
            timeout={200}
            unmountOnExit
          >
            <Box sx={{ display: 'flex' }}>
              <ToggleButtonGroup
                value={selectedPeriodDays}
                exclusive
                onChange={handlePeriodChange}
                size="small"
                sx={createSegmentedControlSx(72)}
              >
                {PERIOD_OPTIONS.map((periodDays) => (
                  <ToggleButton
                    key={periodDays}
                    value={periodDays}
                    aria-label={`${periodDays} jours`}
                  >
                    {periodDays} jours
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            </Box>
          </Collapse>
        </Stack>
      </Stack>

      <Box sx={{ position: 'relative', flex: 1, minHeight: 0 }}>
        <Fade in={selectedView === 'graphs'} timeout={200}>
          <Stack
            sx={{
              position: 'absolute',
              inset: 0,
              overflowY: 'auto',
              pointerEvents: selectedView === 'graphs' ? 'auto' : 'none'
            }}
          >
            <Dashboard
              error={emailStats.error}
              loading={emailStats.loading}
              stats={emailStats.stats}
              onSelectSends={handleSelectSends}
            />
          </Stack>
        </Fade>

        <Fade in={selectedView === 'table'} timeout={200} unmountOnExit>
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              minHeight: 0,
              pointerEvents: selectedView === 'table' ? 'auto' : 'none'
            }}
          >
            <HistoryView
              initialSection={initialHistorySection}
              initialSendsSourceType={initialSendsSourceType}
            />
          </Box>
        </Fade>
      </Box>
    </Stack>
  )
}

export default Mailer
